'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import CurrencyDropdown from './CurrencyDropdown';
import axios from 'axios';
import debounce from 'lodash.debounce';
import { formatNumber } from '@/utils/coins/formatNumber';
import { LoadingSpinner } from '@/components/Icons';
import { ArrowUpDown } from 'lucide-react';
import { CoinProps } from '@/types/coins';

interface CurrencyExchangeCalculatorProps {
    selectedCurrency: CoinProps | undefined;
    usdtToToman: number;
}

const getPriceToman = (coin: CoinProps | null): number => {
    if (!coin) return 0;
    if (coin.symbol === 'TMN') return 1;
    if (typeof coin.price === 'number') return coin.price;
    const p = coin.price as any;
    if (typeof p === 'number') return p;
    if (p?.ir_price_buy) return parseFloat(p.ir_price_buy);
    if (p?.price_buy && p?.usdt_price) return p.price_buy * p.usdt_price;
    return 0;
};

// The calculate API only knows crypto; gold & fiat are priced in toman already
const isTomanAsset = (coin: CoinProps) =>
    ['gold', 'currency'].includes(String(coin.type)) || coin.typeId === 2 || coin.typeId === 3;

/** Conversion through the toman prices both sides already carry; 0 when a price is unknown */
const convertViaToman = (
    source: CoinProps | null,
    target: CoinProps | null,
    amount: number,
): number => {
    const sourceToman = getPriceToman(source);
    const targetToman = getPriceToman(target);
    return sourceToman > 0 && targetToman > 0 ? (amount * sourceToman) / targetToman : 0;
};

const CurrencyExchangeCalculator: React.FC<CurrencyExchangeCalculatorProps> = ({
    selectedCurrency,
    usdtToToman,
}) => {
    const usdtToTomanCurrency: CoinProps = useMemo(
        () => ({
            name: 'Toman',
            name_fa: 'تومان',
            symbol: 'TMN',
            iconUrl: '/images/toman.png',
            price: {
                price_buy: usdtToToman,
            },
        }),
        [usdtToToman],
    );

    const [sourceCurrency, setSourceCurrency] = useState<CoinProps | null>(
        selectedCurrency || null,
    );
    const [targetCurrency, setTargetCurrency] = useState<CoinProps | null>(usdtToTomanCurrency);
    const [sourceAmount, setSourceAmount] = useState<number>(1);
    // First answer computed at render, so the server HTML already shows «۱ دلار = … تومان»
    // instead of a zero that the browser fixes later
    const [targetAmount, setTargetAmount] = useState<number>(() =>
        convertViaToman(selectedCurrency || null, usdtToTomanCurrency, 1),
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [arrowDown, setArrowDown] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const debouncedFetchExchangeRate = useMemo(() => {
        return debounce(
            async (sourceCoin: CoinProps | null, targetCoin: CoinProps | null, amount: number) => {
                if (!sourceCoin || !targetCoin) return;

                const sourceToman = getPriceToman(sourceCoin);
                const targetToman = getPriceToman(targetCoin);

                if (
                    (isTomanAsset(sourceCoin) || isTomanAsset(targetCoin)) &&
                    sourceToman > 0 &&
                    targetToman > 0
                ) {
                    // No request, no spinner: the answer is known at once (also at hydration)
                    setTargetAmount((amount * sourceToman) / targetToman);
                    return;
                }

                setIsLoading(true);
                try {
                    const response = await axios.get('/api/coin/calculate/', {
                        params: {
                            coin: sourceCoin.symbol || '',
                            base: targetCoin.symbol || '',
                            amount: amount,
                        },
                    });

                    if (response.data && Number(response.data) > 0) {
                        setTargetAmount(Number(response.data));
                    } else if (sourceToman > 0 && targetToman > 0) {
                        setTargetAmount((amount * sourceToman) / targetToman);
                    }
                } catch {
                    if (sourceToman > 0 && targetToman > 0) {
                        setTargetAmount((amount * sourceToman) / targetToman);
                    } else {
                        setTargetAmount(0);
                    }
                } finally {
                    setIsLoading(false);
                }
            },
            300,
        );
    }, []);

    useEffect(() => {
        if (sourceCurrency && targetCurrency) {
            debouncedFetchExchangeRate(sourceCurrency, targetCurrency, sourceAmount);
        }
    }, [sourceCurrency, targetCurrency, sourceAmount, debouncedFetchExchangeRate]);

    useEffect(() => {
        const fetchRate = debouncedFetchExchangeRate;
        return () => {
            fetchRate.cancel();
        };
    }, [debouncedFetchExchangeRate]);

    const handleSourceAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const amount = parseFloat(e.target.value) || 0;
        setSourceAmount(amount);
    };

    const handleSwapCurrencies = () => {
        setSourceCurrency(targetCurrency);
        setTargetCurrency(sourceCurrency);
        setArrowDown((prev) => !prev);
    };

    const presets = [1, 10, 100, 1000];
    // Toman value of one unit of the page's symbol (server-rendered, read by crawlers)
    const unitRate = convertViaToman(selectedCurrency || null, usdtToTomanCurrency, 1);

    return (
        <div
            id='symbol-calculator-section'
            className='bg-white dark:bg-primary-active -mx-4 sm:-mx-6 lg:mx-0 px-4 sm:px-6 lg:p-6 py-5 lg:py-6 border-0 lg:border border-gray-100 dark:border-white/10 lg:rounded-2xl shadow-none lg:shadow-sm'
        >
            {/* Title Header */}
            <div className='mb-5 pb-3 border-b border-gray-100 dark:border-white/5'>
                <h3 className='font-bold text-base sm:text-lg text-gray-900 dark:text-white'>
                    ماشین حساب و مبدل آنلاین {sourceCurrency?.name_fa || sourceCurrency?.name || ''}
                </h3>
                {unitRate > 0 && (
                    <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                        هر ۱ {selectedCurrency?.name_fa || selectedCurrency?.name} ={' '}
                        <bdi dir='ltr'>{formatNumber(unitRate, false, true, 'TMN', true)}</bdi>{' '}
                        تومان
                    </p>
                )}
            </div>

            {/* Input & Output Control Area */}
            <div className='grid grid-cols-1 md:grid-cols-2 md:gap-6 relative items-center'>
                {/* Source Box */}
                <div
                    onClick={() => inputRef.current?.focus()}
                    className='group flex flex-col gap-2.5 bg-gray-50/70 dark:bg-white/[0.04] hover:bg-gray-100/80 dark:hover:bg-white/[0.07] border border-gray-200/80 dark:border-white/10 p-4 sm:p-5 rounded-2xl transition-all duration-300 shadow-xs focus-within:border-blue-500/80 focus-within:ring-2 focus-within:ring-blue-500/15 cursor-pointer'
                >
                    <div className='flex items-center justify-between gap-2 border-b border-gray-200/60 dark:border-white/10 pb-2 transition-colors group-focus-within:border-blue-500/50'>
                        <label
                            htmlFor='source-amount-input'
                            onClick={() => inputRef.current?.focus()}
                            className='text-xs sm:text-sm text-gray-600 dark:text-gray-300 font-semibold cursor-pointer select-none group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors'
                        >
                            مبدا (تبدیل از)
                        </label>
                        <div onClick={(e) => e.stopPropagation()}>
                            <CurrencyDropdown
                                usdtToTomanCurrency={usdtToTomanCurrency}
                                onSelectCurrency={setSourceCurrency}
                                sourceCurrency={sourceCurrency}
                                currentCoin={sourceCurrency}
                                isTarget={false}
                            />
                        </div>
                    </div>
                    <div className='flex items-center pt-1'>
                        <input
                            id='source-amount-input'
                            ref={inputRef}
                            type='number'
                            value={sourceAmount}
                            onChange={handleSourceAmountChange}
                            className='w-full bg-transparent text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white focus:outline-none placeholder-gray-400 dir-ltr text-right transition-all border-b-2 border-transparent focus:border-blue-500/80 pb-1'
                            placeholder='1'
                        />
                    </div>
                </div>

                {/* Swap Action Icon */}
                <div className='md:absolute md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 flex justify-center z-10 my-[-8px] md:my-0'>
                    <button
                        type='button'
                        onClick={handleSwapCurrencies}
                        title='تعویض مبدا و مقصد'
                        aria-label='swap_currencies'
                        className={`p-3 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white shadow-md transition-all duration-300 transform hover:scale-105 active:scale-95 flex items-center justify-center cursor-pointer border border-blue-500`}
                    >
                        <ArrowUpDown
                            className={`size-4 sm:size-5 transition-transform duration-300 ${arrowDown ? 'rotate-180' : ''}`}
                        />
                    </button>
                </div>

                {/* Target Box */}
                <div
                    onClick={() => inputRef.current?.focus()}
                    className='group flex flex-col gap-2.5 bg-gray-50/70 dark:bg-white/[0.04] hover:bg-gray-100/80 dark:hover:bg-white/[0.07] border border-gray-200/80 dark:border-white/10 p-4 sm:p-5 rounded-2xl transition-all duration-300 shadow-xs cursor-pointer'
                >
                    <div className='flex items-center justify-between gap-2 border-b border-gray-200/60 dark:border-white/10 pb-2'>
                        <span
                            onClick={() => inputRef.current?.focus()}
                            className='text-xs sm:text-sm text-gray-600 dark:text-gray-300 font-semibold cursor-pointer select-none group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors'
                        >
                            مقصد (تبدیل به)
                        </span>
                        <div onClick={(e) => e.stopPropagation()}>
                            <CurrencyDropdown
                                usdtToTomanCurrency={usdtToTomanCurrency}
                                onSelectCurrency={setTargetCurrency}
                                isTarget={true}
                                sourceCurrency={sourceCurrency}
                                currentCoin={targetCurrency}
                            />
                        </div>
                    </div>
                    <div className='flex items-center min-h-[40px] pt-1 border-b-2 border-transparent pb-1'>
                        {isLoading ? (
                            <div className='flex items-center gap-2 text-xs text-gray-400'>
                                <LoadingSpinner className='size-5 animate-spin text-blue-500' />
                                <span>در حال محاسبه...</span>
                            </div>
                        ) : (
                            <div className='flex items-baseline gap-1.5 text-2xl sm:text-3xl font-extrabold text-emerald-600 dark:text-emerald-400 truncate dir-ltr text-right w-full'>
                                <span className='truncate'>
                                    {formatNumber(
                                        targetAmount,
                                        false,
                                        true,
                                        targetCurrency?.symbol === 'TMN' ? 'TMN' : 'USD',
                                        true,
                                    )}
                                </span>
                                {targetCurrency?.symbol === 'USDT' && (
                                    <span className='text-sm text-gray-500 font-normal'>$</span>
                                )}
                                {targetCurrency?.symbol === 'TMN' && (
                                    <span className='text-xs sm:text-sm font-normal text-gray-500 dark:text-gray-400'>
                                        تومان
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Quick Presets */}
            <div className='flex flex-wrap items-center justify-between gap-2 mt-5 pt-3.5 border-t border-gray-100 dark:border-white/5 text-xs text-gray-500 dark:text-gray-400'>
                <span className='font-medium'>مقادیر سریع:</span>
                <div className='flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0'>
                    {presets.map((val) => (
                        <button
                            key={val}
                            type='button'
                            onClick={() => setSourceAmount(val)}
                            className={`px-3 py-1.5 rounded-xl border text-xs font-medium transition-all cursor-pointer ${
                                sourceAmount === val
                                    ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                                    : 'bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10 hover:bg-gray-100 dark:hover:bg-white/10 text-gray-700 dark:text-gray-300'
                            }`}
                        >
                            {val} {sourceCurrency?.symbol || ''}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CurrencyExchangeCalculator;
